import * as Yup from 'yup';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Grid from '@mui/system/Unstable_Grid';
import { Switch, MenuItem, TextField, Typography, FormHelperText, FormControlLabel } from '@mui/material';

import { useTranslate } from 'src/locales';
import { useGetConfigByEntityTypeQuery, useGetCustomFieldsForEntityQuery, useUpdateCustomFieldValueMutation } from 'src/services/custom-fields/custom-field.service';
import {
    ICustomField,
    CustomFieldType,
    CustomFieldValue,
    ICustomFieldConfig,
    CustomFieldEntityType,
    UpdateCustomFieldValueDto,
} from 'src/services/custom-fields/custom-field.types';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import { useOrgTenant } from './auth/use-org-tenant';
import { useAuthContext } from './auth/use-auth-context';

// ----------------------------------------------------------------------

interface UseCustomFieldsProps {
    entityType: CustomFieldEntityType;
    entityId?: string;
}

interface CustomFieldFormData {
    [key: string]: any;
}

interface CustomFieldValidationSchema {
    [key: string]: Yup.Schema;
}

interface CustomFieldValidationResult {
    isValid: boolean;
    errors: string[];
}

// interface for hook return type
export interface UseCustomFieldsReturn {
    customFieldConfigs: ICustomFieldConfig[];
    isLoading: boolean;
    fieldValues: CustomFieldFormData;
    renderCustomFields: JSX.Element | (JSX.Element | null)[] | null
    validate: () => CustomFieldValidationResult;
    getData: () => UpdateCustomFieldValueDto | undefined;
    getFields: () => { label: string, value: string }[];
    updateFields: (entityId: string, data: UpdateCustomFieldValueDto) => Promise<any>;
    getFieldValues: () => CustomFieldFormData;
    setFieldValue: (fieldName: string, value: any) => void;
    resetFields: () => void;
    hasCustomFields: boolean;
}

export function useCustomFields({ entityType, entityId }: UseCustomFieldsProps): UseCustomFieldsReturn {

    const { t } = useTranslate();

    const { canAccess } = useFeatures();

    const tenantType = useOrgTenant();

    const authContext = useAuthContext();

    // Fetch custom field configurations
    const { data: customFieldConfigs, isLoading: isLoadingConfigs } = useGetConfigByEntityTypeQuery({
        entityType,
    }, {
        skip: !entityType || !authContext || !canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) || tenantType === TenantType.Candidate,
        refetchOnMountOrArgChange: true,
    });

    // Fetch existing custom field values if entityId is provided
    const { data: existingCustomFields, isLoading: isLoadingValues } = useGetCustomFieldsForEntityQuery(
        { entityType, entityId: entityId! },
        {
            skip: !entityType || !entityId || !authContext || !canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) || tenantType === TenantType.Candidate,
            refetchOnMountOrArgChange: true,
        }
    );

    const [updateFieldValues] = useUpdateCustomFieldValueMutation();

    // Internal state for custom field values
    const [fieldValues, setFieldValues] = useState<CustomFieldFormData>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

    const isLoading = isLoadingConfigs || (entityId ? isLoadingValues : false);

    // Initialize field values when data is loaded
    useEffect(() => {
        if (!customFieldConfigs || isLoading) return;

        const initialValues: CustomFieldFormData = {};

        customFieldConfigs.forEach((config) => {
            const fieldName = `custom_field_${config.id}`;

            // check if we have existing values for this field
            const existingField = existingCustomFields?.find(
                (field: ICustomField) => field.config.id === config.id
            );

            if (existingField) {
                // use existing value
                switch (config.field_type) {
                    case CustomFieldType.TEXT:
                        initialValues[fieldName] = existingField.value.text || '';
                        break;

                    case CustomFieldType.SELECT:
                        initialValues[fieldName] = existingField.value.text || '';
                        break;

                    case CustomFieldType.BOOLEAN:
                        initialValues[fieldName] = existingField.value.boolean || false;
                        break;

                    default:
                        initialValues[fieldName] = existingField.value.json || null;
                }
            } else {
                // use default values
                switch (config.field_type) {
                    case CustomFieldType.TEXT:
                        initialValues[fieldName] = '';
                        break;

                    case CustomFieldType.SELECT:
                        initialValues[fieldName] = '';
                        break;

                    case CustomFieldType.BOOLEAN:
                        initialValues[fieldName] = false;
                        break;

                    default:
                        initialValues[fieldName] = null;
                }
            }
        });

        setFieldValues(initialValues);
    }, [customFieldConfigs, existingCustomFields, isLoading]);

    const hasCustomFields = useMemo(() => {
        let result = Boolean(customFieldConfigs && customFieldConfigs.length > 0);

        if (!canAccess(FeatureType.CUSTOM_FIELDS_ENABLED) || tenantType === TenantType.Candidate) {
            // if custom fields are not enabled or tenant type is Candidate, disable custom fields
            result = false;
        }

        return result;
    }, [customFieldConfigs, canAccess, tenantType]);

    // handle field value changes
    const handleFieldChange = useCallback((fieldName: string, value: any) => {
        setFieldValues(prev => ({
            ...prev,
            [fieldName]: value
        }));

        // clear error when user starts typing
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => ({
                ...prev,
                [fieldName]: ''
            }));
        }
    }, [fieldErrors]);

    const getFields = useCallback((): { label: string, value: string }[] => {
        if (!hasCustomFields || !customFieldConfigs) return [];

        return customFieldConfigs.map((config) => {
            const fieldName = `custom_field_${config.id}`;
            let fieldValue = "";

            switch (config.field_type) {
                case CustomFieldType.TEXT:
                case CustomFieldType.SELECT:
                    fieldValue = fieldValues[fieldName] || '';
                    break;
                case CustomFieldType.BOOLEAN:
                    fieldValue = fieldValues[fieldName] ? 'Yes' : 'No';
                    break;
                default:
                    fieldValue = fieldValues[fieldName] || '';
            }

            return {
                label: config.label,
                value: fieldValue,
            };
        });
    }, [customFieldConfigs, fieldValues, hasCustomFields]);

    // render custom field components
    const renderCustomFields = useMemo(() => {
        if (!customFieldConfigs || customFieldConfigs.length === 0) {
            return null;
        }

        if (isLoading) {
            return (
                <Grid xs={12} sx={{ textAlign: 'center', padding: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                        Loading custom fields...
                    </Typography>
                </Grid>
            )
        }

        return customFieldConfigs.map((config) => {
            const fieldName = `custom_field_${config.id}`;
            const fieldValue = fieldValues[fieldName] || '';
            const fieldError = fieldErrors[fieldName];

            switch (config.field_type) {
                case CustomFieldType.TEXT:
                    return (
                        <Grid xs={12} key={config.id}>
                            <TextField
                                name={fieldName}
                                label={config.label}

                                fullWidth
                                value={fieldValue}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                error={!!fieldError}
                                helperText={fieldError}
                            />
                        </Grid>
                    );

                case CustomFieldType.SELECT:
                    return (
                        <Grid xs={12} key={config.id}>
                            <TextField
                                select
                                name={fieldName}
                                label={config.label}
                                required={config.is_required}
                                fullWidth
                                value={fieldValue}
                                onChange={(e) => handleFieldChange(fieldName, e.target.value)}
                                error={!!fieldError}
                                helperText={fieldError}
                            >
                                {!config.is_required && (
                                    <MenuItem value="">
                                        {t('common.none')}
                                    </MenuItem>
                                )}
                                {config.options?.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    );

                case CustomFieldType.BOOLEAN:
                    return (
                        <Grid xs={12} key={config.id}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={!!fieldValue}
                                        onChange={(e) => handleFieldChange(fieldName, e.target.checked)}
                                        name={fieldName}
                                    />
                                }
                                label={config.label}
                            />
                            {fieldError && (
                                <FormHelperText error>{fieldError}</FormHelperText>
                            )}
                        </Grid>
                    );

                default:
                    return null;
            }
        });
    }, [customFieldConfigs, t, isLoading, fieldValues, fieldErrors, handleFieldChange]);

    // Validate custom fields function
    const validate = useCallback((): CustomFieldValidationResult => {
        const errors: { [key: string]: string } = {};
        const errorMessages: string[] = [];

        if (!customFieldConfigs) {
            return { isValid: true, errors: [] };
        }

        customFieldConfigs.forEach((config) => {
            const fieldName = `custom_field_${config.id}`;
            const fieldValue = fieldValues[fieldName];

            if (config.is_required) {
                switch (config.field_type) {
                    case CustomFieldType.TEXT:
                    case CustomFieldType.SELECT:
                        if (!fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')) {
                            const errorMsg = t('validation.required');
                            errors[fieldName] = errorMsg;
                            errorMessages.push(`${config.label} ${errorMsg}`);
                        }
                        break;

                    case CustomFieldType.BOOLEAN:
                        // boolean fields are typically not required in the traditional sense
                        // as they always have a value (true/false)
                        break;
                    default:
                        if (fieldValue === null || fieldValue === undefined) {
                            const errorMsg = t('validation.required');
                            errors[fieldName] = errorMsg;
                            errorMessages.push(`${config.label} ${errorMsg}`);
                        }
                }
            }
        });

        setFieldErrors(errors);

        return {
            isValid: errorMessages.length === 0,
            errors: errorMessages,
        };
    }, [customFieldConfigs, t, fieldValues]);

    // gt custom field data in API format
    const getData = useCallback((): UpdateCustomFieldValueDto | undefined => {
        if (!customFieldConfigs || !hasCustomFields) {
            return undefined;
        }

        const fields = customFieldConfigs
            .map((config) => {
                const fieldName = `custom_field_${config.id}`;
                const fieldValue = fieldValues[fieldName];

                // Skip if field value is empty/null/undefined for non-required fields
                if (!config.is_required && (fieldValue === '' || fieldValue === null || fieldValue === undefined)) {
                    return null;
                }

                const value: CustomFieldValue = {};

                switch (config.field_type) {
                    case CustomFieldType.TEXT:
                        value.text = fieldValue as string;
                        break;

                    case CustomFieldType.SELECT:
                        value.text = fieldValue as string;
                        break;

                    case CustomFieldType.BOOLEAN:
                        value.boolean = fieldValue as boolean;
                        break;

                    default:
                        value.json = fieldValue;
                }

                return {
                    config_id: config.id,
                    value,
                };
            })
            .filter(Boolean) as UpdateCustomFieldValueDto['fields'];

        return { fields };
    }, [customFieldConfigs, fieldValues, hasCustomFields]);

    // get current field values (for compatibility with existing code)
    const getFieldValues = useCallback(() => fieldValues, [fieldValues]);

    // set field values (for external updates)
    const setFieldValue = useCallback((fieldName: string, value: any) => {
        handleFieldChange(fieldName, value);
    }, [handleFieldChange]);

    // reset all field values
    const resetFields = useCallback(() => {
        setFieldValues({});
        setFieldErrors({});
    }, []);

    const updateFields = useCallback(async (entityIdentifier: string, data: UpdateCustomFieldValueDto) => {
        if (!entityIdentifier) {
            throw new Error('Entity ID is required to update custom fields');
        }

        try {
            const response = await updateFieldValues({
                entityType,
                entityId: entityIdentifier,
                data,
            }).unwrap();

            return response;
        } catch (error) {
            console.error('Failed to update custom fields:', error);
            throw error;
        }
    }, [entityType, updateFieldValues]);

    return {
        // data
        customFieldConfigs: customFieldConfigs || [],
        isLoading,

        // validation
        fieldValues, // Return current field values instead

        // rendering
        renderCustomFields,

        // main functions for parent components
        validate,
        getData,
        getFields,
        updateFields,

        // new utility functions
        getFieldValues,
        setFieldValue,
        resetFields,

        // utilities
        hasCustomFields,
    };
}